import type { NodeV2 } from '@vedalib/editor'
import { getNodeUsages } from '@vedalib/editor'
import type { EditorElement } from '@vedalib/editor/lib/elements'
import * as R from 'ramda'
import React, { useCallback, useMemo, useState } from 'react'

import { getNodeLabel } from 'components/Editor/NodeV2/getNodeLabel'
import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import { IconButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import Empty from 'components/uiKit/Empty'
import { KitSize } from 'components/uiKit/KitTypes'
import Search from 'components/uiKit/Search'
import { EMPTY_ARRAY } from 'constants/commonConstans'
import { updateBlock } from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { omitNode } from 'services/Store/Project/mutators/node'
import { getNotUsedNodes } from 'services/Store/Project/selectors'
import type { Block, MapType } from 'services/Store/Project/types'
import { t } from 'services/Translation'

import TileNode from '../TileNode'
import * as s from './TabStash.module.scss'

interface ITabStashProps {
  block: Block
  parent: NodeV2
  onAdd: (
    node: NodeV2,
    data?: { nodes?: MapType<NodeV2>; elements?: MapType<EditorElement> },
  ) => void
}

const TabStash: React.FC<ITabStashProps> = ({ block, parent, onAdd }) => {
  const dispatch = useProjectDispatch()
  const [search, setSearch] = useState<string>()
  const notUsed = useProjectContext(getNotUsedNodes, block?.uuid)
  const { notUsedElements = EMPTY_ARRAY, notUsedNodes = EMPTY_ARRAY } = block
    ? getNodeUsages(block)
    : {}
  const items = useMemo(
    () =>
      notUsed
        .map((item) => ({ ...item, label: item.label || getNodeLabel(block, item) }))
        .filter((item) => !search || item.label?.toLowerCase().includes(search.toLowerCase())),
    [block, notUsed, search],
  )
  const onChange = useCallback(
    (block: Block) => dispatch(updateBlock({ id: block?.uuid, value: block })),
    [dispatch],
  )

  const removeAllNotUsed = useCallback(() => {
    if (block) {
      onChange(
        R.compose(
          R.assocPath(['elements'], R.omit(notUsedElements.map(R.prop('id')), block.elements)),
          R.assocPath(
            ['schema', 'nodes'],
            R.omit(notUsedNodes.map(R.prop('id')), block.schema.nodes),
          ),
        )(block) as Block,
      )
    }
  }, [block, notUsedElements, notUsedNodes, onChange])

  const handleDeleteNode = useCallback(
    (item: NodeV2) => {
      if (block) {
        onChange(
          R.compose(
            omitNode(item.id),
            // R.assocPath(['elements'], R.omit([item.elementId], block.elements)),
            // R.assocPath(['schema', 'nodes'], R.omit([item.id], block.schema.nodes)),
          )(block) as Block,
        )
      }
    },
    [block, onChange],
  )

  return (
    <div className={s.root}>
      <Padding bottom sides top>
        <div className={s.title}>
          <Search
            disabled={!notUsed.length}
            name='elements'
            onChange={setSearch}
            value={search}
            width='100%'
          />
          <Tooltip overlay={t('uiKit.button.deleteAll')}>
            <IconButton
              disabled={!notUsed.length}
              icon='trashDeleteBin'
              iconSize={KitSize.S}
              name='removeElement'
              onClick={removeAllNotUsed}
              styleType='secondary'
            />
          </Tooltip>
        </div>
      </Padding>
      <LayoutScroll sizeAutoCapable>
        <Padding bottom gap sides>
          <div className={s.items}>
            {items.map((item) => (
              <TileNode
                block={block}
                item={item}
                key={item.id}
                onAdd={onAdd}
                onDelete={handleDeleteNode}
                parent={parent}
                stash
              />
            ))}
            {items.length === 0 && <Empty />}
          </div>
        </Padding>
      </LayoutScroll>
    </div>
  )
}

export default TabStash
